import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { selectTracks } from '@/store/tracks/selectors';

import { Button, Collapse, ConfigProvider, Empty, Flex, Progress, Skeleton, Tag, Tooltip, Typography } from 'antd';
import BadgeModal from '@/components/BadgeModal';
import { GiftOutlined, LockOutlined } from '@ant-design/icons';

import MaterialCard from './MaterialCard';

import BadgeElement from '@shared/components/BadgeElement';
import useIsMobile from '@shared/hooks/useIsMobile';

import classNames from 'classnames';
import dayjs from 'dayjs';

import css from './TrackContent.module.scss';

import { EXPIRED, FINISHED } from '@/constants/study-plan-statuses';

const { Title, Text } = Typography;
const DATE_FORMAT = 'DD.MM.YYYY';

const TrackContent = () => {
  const { t } = useTranslation('TrackId');
  const { trackSections, sectionConsecutive, isLoadingSections, trackStudentPlanStatus, sectionsRewards } =
    useSelector(selectTracks);

  const { id: trackId } = useParams();
  const [isMobile] = useIsMobile(769);
  const [activeSections, setActiveSections] = useState([]);
  const [badgeModalData, setBadgeModalData] = useState([]);

  const calculateSectionPercentage = allTopics => {
    const totalTopics = allTopics.length;
    const finishedTopics = allTopics.filter(topic => topic.userPlanStatus === FINISHED).length;

    return totalTopics === 0 ? 0 : Math.round((finishedTopics / totalTopics) * 100);
  };

  const isSectionCompleted = section => section.topics.every(topic => topic.userPlanStatus === FINISHED);

  const lockedSectionIds = useMemo(() => {
    return trackSections.reduce((lockedIds, section, index) => {
      const isPreviousSectionCompleted = index === 0 || isSectionCompleted(trackSections[index - 1]);
      const isLocked = sectionConsecutive && (lockedIds.length > 0 || !isPreviousSectionCompleted);

      if (isLocked) {
        lockedIds.push(section?.id);
      }

      return lockedIds;
    }, []);
  }, [trackSections, sectionConsecutive]);

  // Проверка заблокированных материалов
  const lockedTopicIds = useMemo(() => {
    return trackSections.reduce((lockedTopicIds, section) => {
      // Если секция заблокирована, все её топики считаются заблокированными
      if (lockedSectionIds.includes(section?.id)) {
        section.topics.forEach(topic => {
          lockedTopicIds.push(topic.id);
        });
      } else if (section?.topicConsecutive) {
        // Если последовательное прохождение топиков, то проверяем каждый топик
        section.topics.reduce((isPreviousTopicCompleted, topic) => {
          // Если предыдущее условие завершено для первой итерации или проверяемого топика
          if (!isPreviousTopicCompleted) {
            lockedTopicIds.push(topic.id);
            return false;
          }
          // Возвращаем, завершён ли текущий топик для следующей проверки
          return topic.userPlanStatus === FINISHED || topic.userPlanStatus === EXPIRED;
        }, true); // Начинаем с того, что предыдущее завершено (для первого топика)
      }
      return lockedTopicIds;
    }, []);
  }, [trackSections, lockedSectionIds]);

  const renderTags = section => {
    const renderTagsByKey = key =>
      section?.[key].map(tag => (
        <Tag key={tag.id} className={css['TrackContent-section-tag']} color='var(--personalColor)'>
          {tag?.name}
        </Tag>
      ));

    if (Array.isArray(section?.skills) && section?.skills?.length > 0) {
      return renderTagsByKey('skills');
    } else if (Array.isArray(section?.competences) && section?.competences?.length > 0) {
      return renderTagsByKey('competences');
    } else return <></>;
  };

  const changeBadgeModalData = (data = []) => {
    setBadgeModalData(data);
  };

  // Ренрер шапки коллапса
  const renderTrackHeader = section => {
    const isLocked = lockedSectionIds.includes(section.id);
    const isSectionActive = activeSections.includes(String(section.id));

    const tagDeadline = section?.deadlineDate && trackStudentPlanStatus && (
      <Tag className={css['TrackContent-section-deadline']} color='blue'>
        {t('deadline', { deadline: dayjs(section?.deadlineDate).format(DATE_FORMAT) })}
      </Tag>
    );

    const everyBadgeRewarded = section?.rewardsDto?.every(reward => reward?.isRewarded);

    // Награда в шапке
    const renderTrackReward = () => {
      if (section?.rewardsDto?.length === 1) {
        const badge = section?.rewardsDto?.[0];
        const size = isMobile ? 'small' : 'middle';

        return <BadgeElement data={badge} disabled={!badge?.isRewarded} hideName tooltipKey='name' size={size} />;
      } else if (section?.rewardsDto?.length > 1) {
        return (
          <Tooltip title={!isMobile && t('previewRewards')}>
            <Button
              shape='circle'
              className={css['TrackContent-section-reward-multiple']}
              icon={<GiftOutlined />}
              type={everyBadgeRewarded ? 'primary' : 'default'}
              onClick={e => {
                e.stopPropagation();
                changeBadgeModalData(section?.rewardsDto);
              }}
            />
          </Tooltip>
        );
      } else return <></>;
    };

    return (
      <Flex gap={isMobile ? 12 : 24}>
        {/* Награда */}
        {sectionsRewards?.length > 0 && (
          <div
            className={classNames(css['TrackContent-section-reward'], {
              [css['TrackContent-section-reward-line']]: isSectionActive && section?.rewardsDto?.length > 0,
              [css['TrackContent-section-reward-line-active']]: everyBadgeRewarded,
            })}
          >
            {renderTrackReward()}
          </div>
        )}

        <Flex
          className={css['TrackContent-section-header']}
          align={isMobile ? 'flex-start' : 'center'}
          justify='space-between'
          vertical={isMobile}
          gap={isMobile && 16}
        >
          <Flex gap={16} align={isMobile ? 'flex-start' : 'center'} vertical={isMobile}>
            <div className={css['TrackContent-section-progress-container']}>
              <Progress
                percent={isLocked ? 0 : calculateSectionPercentage(section?.topics)}
                showInfo={!isLocked}
                className={css['TrackContent-section-progress']}
                type='circle'
                size={isMobile ? 72 : 48}
              />
              {isLocked && <LockOutlined className={css['TrackContent-section-progress-locked']} />}
            </div>

            {/* Deadline для mobile */}
            {isMobile && tagDeadline}
            <Title className={css['TrackContent-section-title']} level={3} data-qa='trackSectionTitle'>
              {section?.name}
            </Title>
          </Flex>

          {/* Deadline для desktop */}
          {!isMobile && tagDeadline}
        </Flex>
      </Flex>
    );
  };

  // Рендер контента коллапса
  const renderTrackContent = section => {
    // Рендерим карточки материалов
    const trackMaterials =
      Array.isArray(section?.topics) &&
      section?.topics?.length > 0 &&
      section?.topics.map(topic => {
        const isLocked = lockedTopicIds?.includes(topic?.id);

        return <MaterialCard key={topic?.id} topic={topic} trackId={trackId} isLocked={isLocked} />;
      });

    const sectionTags = renderTags(section);

    return (
      <div
        className={classNames(css['TrackContent-section-content-body'], {
          [css['TrackContent-section-content-body-reward']]: sectionsRewards?.length > 0,
          [css['TrackContent-section-content-body-reward-line']]: section?.rewardsDto?.length > 0,
          [css['TrackContent-section-content-body-reward-line-active']]: section?.rewardsDto?.every(
            reward => reward?.isRewarded
          ),
        })}
      >
        <Flex className={css['TrackContent-section-content']} vertical gap={16}>
          {/* Теги */}
          <Flex wrap gap={10}>
            {sectionTags}
          </Flex>

          {/* Описание */}
          <Text className={css['TrackContent-section-description']} data-qa='trackSectionDescription'>
            {section?.description}
          </Text>
        </Flex>

        {/* Материалы */}
        <Flex className={css['TrackContent-section-materials']} vertical data-qa='trackMaterialsList'>
          {trackMaterials}
        </Flex>
      </div>
    );
  };

  const trackItems = useMemo(
    () =>
      Array.isArray(trackSections) &&
      trackSections?.length > 0 &&
      trackSections
        .filter(item => item?.topics?.length)
        .map(section => ({
          key: section.id,
          label: renderTrackHeader(section),
          children: renderTrackContent(section),
          className: css['TrackContent-section-content-container'],
        })),
    [trackSections, lockedSectionIds, isMobile, activeSections]
  );

  const renderSections = useMemo(() => {
    if (isLoadingSections) {
      return <Skeleton avatar />;
    } else if (trackItems?.length) {
      return (
        <ConfigProvider
          theme={{
            components: {
              Collapse: {
                contentPadding: isMobile ? 10 : 16,
              },
            },
          }}
        >
          <Collapse
            expandIconPosition='end'
            items={trackItems}
            ghost
            activeKey={activeSections}
            onChange={keys => setActiveSections(keys)}
          />
        </ConfigProvider>
      );
    } else {
      return <Empty description={t('emptySections')} />;
    }
  }, [trackSections, isLoadingSections, trackItems]);

  useEffect(() => {
    const activeKey = trackSections.length === 1 ? trackSections[0]?.id : undefined;

    if (activeKey) {
      setActiveSections([activeKey]);
    }
  }, [trackSections]);

  return (
    <Flex vertical gap={16}>
      <Title className={css['TrackContent-title']} level={3}>
        {t('topics')}
      </Title>

      {/* Секции */}
      {renderSections}

      {/* Модальное окно с наградами если их несколько */}
      {sectionsRewards?.length > 0 && (
        <BadgeModal
          open={!!badgeModalData?.length}
          onCancel={changeBadgeModalData}
          data={badgeModalData}
          showRewarded
        />
      )}
    </Flex>
  );
};

export default TrackContent;
